import { r as registerInstance, c as createEvent, h, d as getElement } from './index-BntG-4Sv.js';
import { L as LoggerProvider, T as TranslationService } from './translation.service-CyWR_ReZ.js';
import { D as DropdownItemAlignment } from './dropdown-item-alignment-DghTf3pJ.js';
import { T as TooltipUtil } from './tooltip-util-HoM2klPV.js';
import { O as OntoTooltipPlacement } from './onto-tooltip-placement-D_J2-9CG.js';
import '@ontotext/workbench-api';

const ontoDropdownCss = ":host{display:block}.onto-dropdown{position:relative;width:fit-content}.onto-dropdown .onto-dropdown-button{display:flex;flex-direction:row;gap:0.3em;align-items:center;font-size:1rem;font-weight:400;padding:0.4em 1rem;line-height:1.5;outline:none;background-color:var(--gw-button-secondary-background);border:1px solid var(--gw-button-secondary-border-color);color:var(--gw-button-secondary-color);transition:all 0.15s ease-out}.onto-dropdown .onto-dropdown-button .button-icon{color:var(--gw-button-secondary-icon-color)}.onto-dropdown .onto-dropdown-button .ri-arrow-down-s-line{font-size:1.1em;transition:all 0.2s ease-in}.onto-dropdown .onto-dropdown-button:disabled{background-color:var(--gw-button-secondary-disabled-background);border:1px solid var(--gw-button-secondary-disabled-border-color);color:var(--gw-button-secondary-disabled-color)}.onto-dropdown.open .onto-dropdown-button{color:var(--gw-button-primary-color);background-color:var(--gw-button-primary-background);border-color:var(--gw-button-primary-border-color)}.onto-dropdown.open .onto-dropdown-button .button-icon{color:var(--gw-button-primary-icon-color)}.onto-dropdown .onto-dropdown-button:hover:not(:disabled){background-color:var(--gw-button-secondary-hover-background);border:1px solid var(--gw-button-secondary-hover-border-color);color:var(--gw-button-secondary-hover-color);cursor:pointer}.onto-dropdown .onto-dropdown-button:hover:not(:disabled) .button-icon{transform:scale(1.2)}.onto-dropdown.open .onto-dropdown-button:hover{color:var(--gw-button-primary-hover-color);background-color:var(--gw-button-primary-hover-background);border-color:var(--gw-button-primary-hover-border-color)}.onto-dropdown .onto-dropdown-menu{min-width:100%;position:absolute;margin:0;padding:0;z-index:1001;overflow-y:auto;max-height:340px;max-width:32rem;color:var(--gw-tieredmenu-color);background-color:var(--gw-tieredmenu-background);border:1px solid var(--gw-tieredmenu-border-color);box-shadow:var(--gw-tieredmenu-shadow-1-offset-x) var(--gw-tieredmenu-shadow-1-offset-y) var(--gw-tieredmenu-shadow-1-blur) var(--gw-tieredmenu-shadow-1-spread) var(--gw-tieredmenu-shadow-1-color)}.onto-dropdown.closed .onto-dropdown-menu{display:none}.onto-dropdown .onto-dropdown-menu-item{display:block;text-decoration:none;font-size:1rem;font-weight:400;padding:0.5em 1.2em;color:var(--gw-tieredmenu-item-color);background-color:var(--gw-tieredmenu-background);text-align:left;white-space:nowrap;border:none;outline:none;width:100%}.onto-dropdown .onto-dropdown-menu-item:hover{cursor:pointer;color:var(--gw-tieredmenu-item-focus-color);background-color:var(--gw-tieredmenu-item-focus-background)}.onto-dropdown .onto-dropdown-menu-item:hover .item-icon{transform:scale(1.2)}.onto-dropdown .onto-dropdown-option-icon{margin-right:0.2em}.onto-dropdown .onto-dropdown-option-image-icon{margin-right:0.4em}.onto-dropdown .onto-dropdown-right-item-alignment{right:0;left:auto}";

const OntoDropdown = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.valueChanged = createEvent(this, "valueChanged");
        this.toggle = createEvent(this, "toggle");
        this.logger = LoggerProvider.logger;
        this.GUIDE_SELECTOR_ATTR = 'guide-selector';
        /**
         * Indicates whether the dropdown menu is open.
         */
        this.open = false;
        /**
         * Holds the content of the tooltip
         */
        this.buttonTooltipContent = '';
        /**
         * Icon class for the main dropdown button.
         */
        this.iconClass = '';
        /**
         * Path or URL to the image used as icon for main dropdown button.
         */
        this.iconImage = '';
        /**
         * Specifies the items tooltip placement. Accepts a string of the placement or a function that returns the placement.
         * The function takes the isOpen parameter as a boolean and returns the placement as a string.
         * If not provided, the tooltip will be placed to the left.
         *
         */
        this.tooltipPlacement = OntoTooltipPlacement.LEFT;
        /**
         * Specifies the dropdown items' alignment. If not provided, the items and the dropdown button will be aligned to the left.
         *
         */
        this.dropdownAlignment = DropdownItemAlignment.LEFT;
        /**
         * Flag to determine if the dropdown should close automatically when a click occurs outside the dropdown.
         */
        this.autoClose = false;
        /**
         * Indicates whether the dropdown is disabled.
         */
        this.disabled = false;
        this.toggleButtonClickHandler = () => {
            TooltipUtil.destroyTooltip(this.dropdownButtonElement);
            this.buttonTooltipContent = '';
            this.clearDropdownItemTooltips();
            this.toggleComponent();
        };
    }
    /**
     * Listens for the Escape key keydown event globally.
     * Closes the dropdown menu if the Escape key is pressed.
     *
     * @param ev The keyboard event triggered by the Escape key.
     */
    keydownListener(ev) {
        if (ev.key === 'Escape') {
            this.closeMenu();
        }
    }
    /**
     * Listens for mouse click events globally.
     * Closes the dropdown menu if the click occurs outside the dropdown.
     *
     * @param ev The mouse event triggered by a click.
     */
    mouseClickListener(ev) {
        const target = ev.target;
        if (this.autoClose && !this.hostElement.contains(target)) {
            this.closeMenu();
        }
    }
    componentDidUpdate() {
        if (this.dropdownButtonElement) {
            if (this.buttonTooltipContent && this.buttonTooltipContent !== '') {
                TooltipUtil.updateTooltipContent(this.dropdownButtonElement, this.buttonTooltipContent);
            }
            else {
                TooltipUtil.destroyTooltip(this.dropdownButtonElement);
            }
        }
    }
    render() {
        var _a, _b;
        const dropdownAlignmentClass = this.dropdownAlignment === DropdownItemAlignment.RIGHT
            ? 'onto-dropdown-right-item-alignment' : 'onto-dropdown-left-item-alignment';
        const tooltipPlacement = typeof this.tooltipPlacement === 'function' ? this.tooltipPlacement(this.open) : this.tooltipPlacement;
        return (h("div", { key: '6096f8fa2aeb5a4926e723bff216b02ada531d95', class: `onto-dropdown ${this.open ? 'open' : 'closed'}` }, h("button", Object.assign({ key: '8671e6f4f1a5f207e0abd6cce3b7fe5679329f17', class: "onto-dropdown-button", ref: (el) => this.dropdownButtonElement = el }, (this.dropdownButtonGuideSelector ? { [this.GUIDE_SELECTOR_ATTR]: this.dropdownButtonGuideSelector } : {}), { "tooltip-placement": tooltipPlacement, "tooltip-content": this.buttonTooltipContent }, (this.tooltipTheme ? { 'tooltip-theme': this.tooltipTheme } : {}), { onMouseEnter: this.setDropdownButtonTooltip(), onClick: this.toggleButtonClickHandler, disabled: this.disabled }), this.iconClass ? h("i", { class: 'button-icon ri-lg ' + this.iconClass }) : '', h("span", { key: 'e3a00e3569e08c814213956be09deaf14312e677', class: 'button-name' }, (_a = this.dropdownButtonName) !== null && _a !== void 0 ? _a : this.translate(this.dropdownButtonNameLabelKey)), h("i", { key: '9889cac91625e65ede13fbd56816f7d3d6f772c0', class: `ri-arrow-down-s-line ${this.open ? 'rotate-180' : ''}` })), h("div", { key: 'caa716b3c5a56017f8b4e6cbf293e702911fa5c7', class: 'onto-dropdown-menu ' + dropdownAlignmentClass }, (_b = this.items) === null || _b === void 0 ? void 0 : _b.map((item) => {
            var _a, _b;
            return h("button", Object.assign({ class: 'onto-dropdown-menu-item ' + item.cssClass }, (item.guideSelector ? { [this.GUIDE_SELECTOR_ATTR]: item.guideSelector } : {}), { "tooltip-placement": OntoTooltipPlacement.LEFT }, (this.tooltipTheme ? { 'tooltip-theme': this.tooltipTheme } : {}), { onMouseEnter: this.setDropdownItemTooltip(item), onClick: this.itemClickHandler(item.value), disabled: this.disabled }), item.iconClass ? h("span", { class: 'onto-dropdown-option-icon ' + item.iconClass }) : '', item.iconImage ? h("img", { class: 'onto-dropdown-option-image-icon', src: item.iconImage, alt: (_a = item.name) !== null && _a !== void 0 ? _a : this.translate(item.nameLabelKey) }) : '', h("span", null, (_b = item.name) !== null && _b !== void 0 ? _b : this.translate(item.nameLabelKey)));
        }))));
    }
    setDropdownButtonTooltip() {
        return async () => {
            var _a;
            let tooltipContent;
            if (typeof this.dropdownButtonTooltip === 'function') {
                tooltipContent = await this.getTooltipContent(this.dropdownButtonTooltip);
            }
            else {
                tooltipContent = (_a = this.dropdownButtonTooltip) !== null && _a !== void 0 ? _a : this.translate(this.dropdownButtonTooltipLabelKey);
            }
            this.buttonTooltipContent = tooltipContent;
        };
    }
    setDropdownItemTooltip(item) {
        return async (event) => {
            var _a;
            const target = event.currentTarget;
            if (typeof item.tooltip === 'function') {
                let tooltipContent = await this.getTooltipContent(item.tooltip);
                target.setAttribute('tooltip-content', tooltipContent);
            }
            else {
                target.setAttribute('tooltip-content', (_a = item.tooltip) !== null && _a !== void 0 ? _a : this.translate(item.tooltipLabelKey));
            }
        };
    }
    clearDropdownItemTooltips() {
        this.hostElement
            .querySelectorAll('.onto-dropdown-menu-item')
            .forEach((el) => {
            TooltipUtil.destroyTooltip(el);
            el.setAttribute('tooltip-content', '');
        });
    }
    itemClickHandler(value) {
        return () => this.onSelect(value);
    }
    translate(key) {
        return key ? TranslationService.translate(key) : '';
    }
    onSelect(value) {
        this.open = false;
        this.toggle.emit(this.open);
        this.valueChanged.emit(value);
    }
    toggleComponent() {
        this.open = !this.open;
        this.toggle.emit(this.open);
    }
    closeMenu() {
        this.open = false;
        this.toggle.emit(this.open);
    }
    async getTooltipContent(tooltipFunction) {
        let tooltipContent = '';
        try {
            tooltipContent = await tooltipFunction();
        }
        catch (error) {
            this.logger.error(error);
        }
        return tooltipContent;
    }
    get hostElement() { return getElement(this); }
};
OntoDropdown.style = ontoDropdownCss;

export { OntoDropdown as onto_dropdown };
